import './QuantitativeObjectiveEditForm.css';

import React, { useContext, useEffect, useState } from 'react';
import B2BFormContainer from '../../../b2b-common/B2BFormContainer';
import { generateSelectField } from '../../../b2b-common/helpers';
import { ReactComponent as RegularObjectiveTypeImage } from '../../../../resources/icons/objectives/regular_obj.svg';
import { ReactComponent as ThresholdAboveObjectiveTypeImage } from '../../../../resources/icons/objectives/threshold_above_obj.svg';
import { ReactComponent as ThresholdBelowObjectiveTypeImage } from '../../../../resources/icons/objectives/threshold_below_obj.svg';
import { ReactComponent as RangeObjectiveTypeImage } from '../../../../resources/icons/objectives/range_obj.svg';
import DatesRangeEditor from '../dates/DatesRangeEditor';
import EditText from '../../../b2b-common/EditText';
import Tolerance from './Tolerance';
import GenericTabs from '../../../b2b-common/GenericTabs';
import Applicability from './Applicability';
import { deleteObjective, saveNewObjective, updateObjective } from '../../../../services/b2b-plans-apiService';
import { LoginContext } from '../../../../App';
import { showNotification } from '../../../b2b-common/Notification';
import { B2BContentContext } from '../../../layout/Layout';
import useB2BTranslation from '../../../b2b-common/hooks/useB2BTranslation';

export const getQuantitativeObjectiveTypes = (objective, t) => {
    const items = [
        { value: 'Regular', label: t('Regular'), default: objective ? objective.objectiveType === 'QUANTITATIVE' : false },
        { value: 'Threshold_Above', label: t('Threshold Above'), default: objective ? objective.objectiveType === 'QUANTITATIVE_THRESHOLD' && objective.startComparisonOperator === 'GREATER_THAN' : false },
        { value: 'Threshold_Below', label: t('Threshold Below'), default: objective ? objective.objectiveType === 'QUANTITATIVE_THRESHOLD' && objective.startComparisonOperator === 'LESS_THAN' : false },
        { value: 'Range', label: t('Range'), default: objective ? objective.objectiveType === 'QUANTITATIVE_THRESHOLD' && objective.startComparisonOperator === 'GREATER_THAN' && objective.endComparisonOperator === 'LESS_THAN' : false },
    ];
    // console.log('getQuantitativeObjectiveTypes:', items);
    return items;
}

export function getObjectiveType(objective) {
    if (objective.objectiveType === 'QUANTITATIVE') {
        return 'Regular';
    } else if (objective.objectiveType === 'QUANTITATIVE_THRESHOLD' && objective.startComparisonOperator === 'GREATER_THAN' && objective.endComparisonOperator === null) {
        return 'Threshold_Above';
    } else if (objective.objectiveType === 'QUANTITATIVE_THRESHOLD' && objective.startComparisonOperator === 'LESS_THAN' && objective.endComparisonOperator === null) {
        return 'Threshold_Below';
    } else if (objective.objectiveType === 'QUANTITATIVE_THRESHOLD' && objective.startComparisonOperator === 'GREATER_THAN' && objective.endComparisonOperator === 'LESS_THAN') {
        return 'Range';
    }
    return 'Regular';
}

const renderSVGImage = (selectedObjectiveType) => {
    switch (selectedObjectiveType) {
        case 'Regular':
            return <RegularObjectiveTypeImage />;
        case 'Threshold_Above':
            return <ThresholdAboveObjectiveTypeImage />;
        case 'Threshold_Below':
            return <ThresholdBelowObjectiveTypeImage />;
        case 'Range':
            return <RangeObjectiveTypeImage />;
        default:
            return null;
    }
};

const QuantitativeObjectiveEditForm = ({
    objective,
    planUUID,
    areaId,
    areaIndicatorId,
    indicator = null,
    startDate,
    endDate,
    disabledDateRanges = [],
    onSave, onDelete, onCancel }) => {

    const initialQuantitativeObjective = {
        "objectiveType": "QUANTITATIVE",
        "startDate": startDate || null,
        "endDate": endDate || null,
        "applicability": null,
        "startQuantitativeValue": 0,
        "endQuantitativeValue": 0
    }

    const { t } = useB2BTranslation('b2b-plans');
    const { loginData } = useContext(LoginContext);
    const { setNotification } = useContext(B2BContentContext);
    const [error, setError] = useState(null);

    const [currentObjective, setCurrentObjective] = useState(objective ? { ...objective, startDate: startDate || objective.startDate, endDate: endDate || objective.endDate } : initialQuantitativeObjective);
    const [selectedObjectiveType, setSelectedObjectiveType] = useState(getObjectiveType(currentObjective));

    useEffect(() => {
        if (startDate && endDate) {
            const newObjective = { ...currentObjective, startDate, endDate };
            setCurrentObjective(newObjective);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        if (objective) {
            setCurrentObjective(objective);
            setCurrentObjective({ ...objective, startDate: startDate || objective.startDate, endDate: endDate || objective.endDate });
            setSelectedObjectiveType(getObjectiveType(objective));
        } else {
            setCurrentObjective(initialQuantitativeObjective);
            setSelectedObjectiveType('Regular');
        }
    }, [objective]);

    const handleObjectiveTypeChange = (event) => {
        const newObjectiveType = event.target.value;
        switch (newObjectiveType) {
            case 'Regular': {
                const { objectiveId, startDate, endDate, startQuantitativeValue, endQuantitativeValue } = currentObjective;
                const newObjective = {
                    objectiveId, startDate, endDate, startQuantitativeValue, endQuantitativeValue,
                    applicability: null,
                    objectiveType: 'QUANTITATIVE'
                };
                setCurrentObjective(newObjective);
                break;
            }
            case 'Threshold_Above': {
                const { objectiveId, startDate, endDate, applicability, startQuantitativeValue } = currentObjective;
                const newObjective = {
                    objectiveId, startDate, endDate, applicability, startQuantitativeValue,
                    endQuantitativeValue: 0,
                    objectiveType: 'QUANTITATIVE_THRESHOLD',
                    startComparisonOperator: 'GREATER_THAN',
                    endComparisonOperator: null,
                    joinComparisonAs: 'AND',
                    penalties: 0.0,
                    penaltiesExpressedAs: 'ABSOLUTE_VALUE',
                };
                setCurrentObjective(newObjective);
                break;
            }
            case 'Threshold_Below': {
                const { objectiveId, startDate, endDate, applicability, startQuantitativeValue } = currentObjective;
                const newObjective = {
                    objectiveId, startDate, endDate, applicability, startQuantitativeValue,
                    endQuantitativeValue: 0,
                    objectiveType: 'QUANTITATIVE_THRESHOLD',
                    startComparisonOperator: 'LESS_THAN',
                    endComparisonOperator: null,
                    joinComparisonAs: 'AND',
                    penalties: 0.0,
                    penaltiesExpressedAs: 'ABSOLUTE_VALUE',
                };
                setCurrentObjective(newObjective);
                break;
            }
            case 'Range': {
                const { objectiveId, startDate, endDate, applicability, startQuantitativeValue, endQuantitativeValue } = currentObjective;
                const newObjective = {
                    objectiveId, startDate, endDate, applicability, startQuantitativeValue, endQuantitativeValue,
                    objectiveType: 'QUANTITATIVE_THRESHOLD',
                    startComparisonOperator: 'GREATER_THAN',
                    endComparisonOperator: 'LESS_THAN',
                    joinComparisonAs: 'AND',
                    penalties: 0.0,
                    penaltiesExpressedAs: 'ABSOLUTE_VALUE',
                };
                setCurrentObjective(newObjective);
                break;
            }
            default:
                break;
        }
        setSelectedObjectiveType(newObjectiveType);
    };

    const handleSave = async () => {
        if (objective === null) { // new objective
            // console.log('Save new objective:', currentObjective);
            const newObjective = { ...currentObjective, objectiveId: null };
            try {
                const savedObjective = await saveNewObjective(loginData.accessToken, planUUID, areaId, areaIndicatorId, newObjective);
                showNotification(setNotification, t('Objective saved'), 'success', 3000);
                // console.log('savedObjective:', savedObjective);
                onSave && onSave(savedObjective);
            } catch (error) {
                setError(error.message);
                showNotification(setNotification, t('Error saving objective: ') + error.message, 'error', 3000);
                console.error('Error saving objective:', error);
            }
        } else { // update objective
            // console.log('Update objective:', currentObjective);
            try {
                const updatedObjective = await updateObjective(loginData.accessToken, planUUID, areaId, areaIndicatorId, currentObjective);
                showNotification(setNotification, t('Objective updated'), 'success', 3000);
                onSave && onSave(updatedObjective);
            } catch (error) {
                setError(error.message);
                console.error('Error updating objective:', error);
                showNotification(setNotification, t('Error updating objective: ') + error.message, 'error', 3000);
            }
        }
    }

    const handleDeleteObjective = async () => {
        // console.log('Delete objective:', currentObjective);
        try {
            await deleteObjective(loginData.accessToken, planUUID, areaId, areaIndicatorId, currentObjective.objectiveId);
            showNotification(setNotification, t('Objective deleted'), 'success', 3000);
            onDelete && onDelete(currentObjective.objectiveId);
        } catch (error) {
            setError(error.message);
            console.error('Error deleting objective:', error);
            showNotification(setNotification, t('Error deleting objective: ') + error.message, 'error', 3000);
        }
    }

    const handleCancel = () => {
        onCancel();
    }

    const measurementUnit = indicator ? indicator.quantitativeMeasurementUnitDTO.quantitativeMeasurementUnit : '';

    const tabsConfig = [
        {
            label: t('Main fields'),
            icon: null,
            content: (
                <div className='objective-edit-form-obj-main-fields'>
                    {startDate && endDate && (
                        <DatesRangeEditor
                            datesRangeHolder={currentObjective}
                            setDatesRangeHolder={setCurrentObjective}
                            startDateLabel={t('Start date')}
                            endDateLabel={t('End date')}
                            startDateField='startDate'
                            endDateField='endDate'
                            className={'objective-edit-form-obj-dates-range'}
                            disabledDateRanges={disabledDateRanges}
                        />
                    )}
                    <div className='objective-edit-form-obj-values'>
                        {selectedObjectiveType === 'Regular' && (
                            <>
                                <EditText object={currentObjective} objectField={'startQuantitativeValue'} setObjectField={setCurrentObjective} label={t('Start value')} className={'objective-edit-form-obj-value'} suffix={measurementUnit} />
                                <EditText object={currentObjective} objectField={'endQuantitativeValue'} setObjectField={setCurrentObjective} label={t('End value')} className={'objective-edit-form-obj-value'} suffix={measurementUnit} />
                            </>
                        )}
                        {((selectedObjectiveType === 'Threshold_Above') || (selectedObjectiveType === 'Threshold_Below')) && (
                            <EditText object={currentObjective} objectField={'startQuantitativeValue'} setObjectField={setCurrentObjective} label={t('Threshold value')} className={'objective-edit-form-obj-value'} suffix={measurementUnit} />
                        )}
                        {selectedObjectiveType === 'Range' && (
                            <>
                                <EditText object={currentObjective} objectField={'startQuantitativeValue'} setObjectField={setCurrentObjective} label={t('Lower range value')} className={'objective-edit-form-obj-value'} suffix={measurementUnit} />
                                <EditText object={currentObjective} objectField={'endQuantitativeValue'} setObjectField={setCurrentObjective} label={t('Higher range value')} className={'objective-edit-form-obj-value'} suffix={measurementUnit} />
                            </>
                        )}
                    </div>
                    {((selectedObjectiveType === 'Threshold_Above') || (selectedObjectiveType === 'Threshold_Below') || (selectedObjectiveType === 'Range')) && (
                        <div className='objective-edit-form-obj-values-fails-allowed'>
                            <Tolerance objective={currentObjective} setObjective={setCurrentObjective} edit={true} />
                        </div>
                    )}
                </div>
            ),
        },
        {
            label: t('Applicability'),
            icon: null,
            content: (
                <Applicability objective={currentObjective} setObjective={setCurrentObjective} edit={true} />
            ),
            showTab: ((selectedObjectiveType === 'Threshold_Above') || (selectedObjectiveType === 'Threshold_Below') || (selectedObjectiveType === 'Range')),
        },
    ];

    return (
        <B2BFormContainer
            title={objective ? t('Edit objective data') : t('New objective data')}
            buttonsConfig={[
                { label: t('Save'), onClick: handleSave },
                ...(objective !== null && objective.objectiveId !== 0 ? [{
                    label: t('Delete'),
                    confirm: true,
                    onConfirm: handleDeleteObjective,
                }] : []),
                { label: t('Cancel'), className: 'b2b-button-cancel white_03', onClick: handleCancel },
            ]}
            className='objective-edit-form b2b-form-01'
        >
            <div className='objective-edit-form-obj-type'>
                {generateSelectField(getQuantitativeObjectiveTypes(currentObjective, t),
                    {
                        label: t('Objective type'),
                        onChange: handleObjectiveTypeChange,
                        value: selectedObjectiveType,
                        className: 'objective-edit-form-obj-type-select',
                    })}
                <div className='objective-edit-form-obj-image'>
                    {renderSVGImage(selectedObjectiveType)}
                </div>
            </div>
            <GenericTabs tabsConfig={tabsConfig} className={'objective-edit-form-obj'} />
        </B2BFormContainer>
    );
}

export default QuantitativeObjectiveEditForm;